import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement,
  Title, 
  Tooltip, 
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getReleaseBugs } from '../../services/testService';
import { getAllFixVersions } from '../../services/apiService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

/**
 * BugComparisonCharts component for displaying bug comparison charts for releases
 * @returns {JSX.Element} BugComparisonCharts component
 */
const BugComparisonCharts = () => {
  // State for releases and bug data
  const [releases, setReleases] = useState([]);
  const [bugData, setBugData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRelease, setSelectedRelease] = useState(null);
  
  // Fetch releases and bug data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all releases
        const releasesResponse = await getAllFixVersions();
        
        let releasesData = [];
        
        // Process releases response based on format
        if (releasesResponse && releasesResponse.data && Array.isArray(releasesResponse.data)) {
          releasesData = releasesResponse.data;
        } else if (releasesResponse && Array.isArray(releasesResponse)) {
          releasesData = releasesResponse;
        } else if (releasesResponse && releasesResponse.data && releasesResponse.data.data && Array.isArray(releasesResponse.data.data)) {
          releasesData = releasesResponse.data.data;
        } else if (releasesResponse && releasesResponse.success && Array.isArray(releasesResponse.data)) {
          releasesData = releasesResponse.data;
        } else {
          throw new Error('Invalid releases response format');
        }
        
        // Filter active releases and sort by version
        const activeReleases = releasesData
          .filter(release => release.active)
          .sort((a, b) => {
            // Parse version strings like 5.9.08
            const versionA = a.version.split('.').map(Number);
            const versionB = b.version.split('.').map(Number);
            
            // Compare major version
            if (versionA[0] !== versionB[0]) return versionA[0] - versionB[0];
            // Compare minor version
            if (versionA[1] !== versionB[1]) return versionA[1] - versionB[1];
            // Compare patch version
            return versionA[2] - versionB[2];
          });
        
        // Get the last 10 releases
        const last10Releases = activeReleases.slice(-10);
        
        setReleases(last10Releases);
        
        // Set the most recent release as the selected release
        if (last10Releases.length > 0) {
          setSelectedRelease(last10Releases[last10Releases.length - 1].version);
        }
        
        // Fetch bug data for each release
        const bugDataMap = {};
        
        for (const release of last10Releases) {
          try {
            const bugResponse = await getReleaseBugs(release.version);
            
            if (bugResponse && bugResponse.data) {
              bugDataMap[release.version] = bugResponse.data;
            }
          } catch (err) {
            console.error(`Error fetching bug data for release ${release.version}:`, err);
            // Continue with other releases even if one fails
          }
        }
        
        setBugData(bugDataMap);
      } catch (err) {
        console.error('Error fetching data for bug comparison charts:', err);
        setError('Failed to load data for bug comparison charts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Prepare chart data for total bugs by priority
  const prepareTotalBugsByPriorityData = () => {
    const labels = releases.map(release => release.version);
    
    // Get all possible priorities
    const allBugs = Object.values(bugData).flat();
    const uniquePriorities = [...new Set(allBugs.map(bug => bug.priority))].filter(priority => priority);
    
    // Sort priorities in order: P1, P2, P3, P4
    const sortedPriorities = uniquePriorities.sort((a, b) => {
      const priorityA = parseInt(a.replace('P', ''));
      const priorityB = parseInt(b.replace('P', ''));
      return priorityA - priorityB;
    });
    
    // Create datasets for each priority
    const datasets = sortedPriorities.map(priority => {
      let color;
      switch(priority) {
        case 'P1':
          color = '#FF0000'; // Red
          break;
        case 'P2':
          color = '#FFA500'; // Orange
          break;
        case 'P3':
          color = '#00B050'; // Green
          break;
        case 'P4':
          color = '#0070C0'; // Blue
          break;
        default:
          color = '#CCCCCC'; // Gray
      }
      
      const data = releases.map(release => {
        const bugs = bugData[release.version] || [];
        return bugs.filter(bug => bug.priority === priority).length;
      });
      
      return {
        label: priority,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Prepare chart data for fix version distribution
  const prepareFixVersionDistributionData = () => {
    const labels = releases.map(release => release.version);
    
    // Define the three categories for fix versions
    const categories = [
      { label: 'Current Version', color: '#4CAF50' }, // Green
      { label: 'Next Version', color: '#FFC107' },    // Amber
      { label: 'Later Versions', color: '#2196F3' }   // Blue
    ];
    
    // Create datasets for each category
    const datasets = categories.map(category => {
      const data = releases.map((release, releaseIndex) => {
        const bugs = bugData[release.version] || [];
        let count = 0;
        
        // Get the next version (if available)
        const nextVersionIndex = releaseIndex + 1 < releases.length ? releaseIndex + 1 : -1;
        const nextVersion = nextVersionIndex >= 0 ? releases[nextVersionIndex].version : null;
        
        bugs.forEach(bug => {
          const fixVersion = bug.fixVersionDummy || 'Unknown';
          
          if (category.label === 'Current Version' && fixVersion === release.version) {
            count++;
          } else if (category.label === 'Next Version' && nextVersion && fixVersion === nextVersion) {
            count++;
          } else if (category.label === 'Later Versions' && 
                    fixVersion !== release.version && 
                    (!nextVersion || fixVersion !== nextVersion)) {
            count++;
          }
        });
        
        return count;
      });
      
      return {
        label: category.label,
        data,
        backgroundColor: category.color,
        borderColor: category.color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Prepare chart data for status distribution
  const prepareStatusDistributionData = () => {
    const labels = releases.map(release => release.version);
    
    // Define the three categories for statuses
    const categories = [
      { 
        label: 'Completed', 
        color: '#1B5E20', // Dark green
        statuses: ['Closed', 'Removed', 'Resolved']
      },
      { 
        label: 'To be Fixed', 
        color: '#FFC107', // Amber
        statuses: ['New', 'Approved']
      },
      { 
        label: 'In Progress', 
        color: '#2196F3', // Blue
        statuses: ['Active', 'In Progress', 'Ready for Test', 'Ready for Review', 'Blocked', 'Deferred']
      }
    ];
    
    // Create datasets for each category
    const datasets = categories.map(category => {
      const data = releases.map(release => {
        const bugs = bugData[release.version] || [];
        return bugs.filter(bug => category.statuses.includes(bug.state)).length;
      });
      
      return {
        label: category.label,
        data,
        backgroundColor: category.color,
        borderColor: category.color,
        borderWidth: 1
      };
    });
    
    return {
      labels,
      datasets
    };
  };
  
  // Chart options for stacked bar chart
  const stackedBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 15,
          padding: 15,
          font: {
            size: 11
          }
        }
      },
      datalabels: {
        color: '#fff',
        anchor: 'center',
        align: 'center',
        formatter: (value) => value > 0 ? value : '',
        font: {
          weight: 'bold',
          size: 11
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          font: {
            size: 10
          }
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Bugs',
          font: {
            size: 11
          }
        },
        ticks: {
          font: {
            size: 10
          }
        }
      }
    },
    layout: {
      padding: {
        top: 10,
        right: 15,
        bottom: 10,
        left: 10
      }
    }
  };
  
  // Chart options for grouped bar chart
  const groupedBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      datalabels: {
        color: '#fff',
        anchor: 'center',
        align: 'center',
        formatter: (value) => value > 0 ? value : '',
        font: {
          weight: 'bold',
          size: 12
        }
      }
    },
    scales: {
      x: {
        stacked: false,
      },
      y: {
        stacked: false,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Bugs'
        }
      }
    }
  };
  
  // Check if there are any releases available
  const hasReleases = releases.length > 0;
  
  // If no data is available, show a message
  const noDataAvailable = !hasReleases || Object.keys(bugData).length === 0;
  
  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : noDataAvailable ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No bug data available for comparison. Please fetch bug data for at least one release first.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Total Bugs by Priority Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Description (Column 1 - 20%) */}
            <Grid item xs={12} md={2}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Total Bugs by Priority
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Shows the number of bugs raised categorized by priority levels (P1, P2, P3, P4) across releases. Helps identify critical issues and prioritize bug fixes.
                </Typography>
              </Paper>
            </Grid>
            
            {/* Chart (Column 2 - 40%) */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Total Bugs Raised by Priority
                </Typography>
                <Box sx={{ height: '320px', width: '100%', position: 'relative', pt: 2 }}>
                  <Bar 
                    data={prepareTotalBugsByPriorityData()} 
                    options={{
                      ...stackedBarOptions,
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        ...stackedBarOptions.plugins,
                        datalabels: {
                          display: false
                        }
                      }
                    }} 
                  />
                </Box>
              </Paper>
            </Grid>
            
            {/* Table (Column 3 - 40%) */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Total Bugs by Priority
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Release</TableCell>
                        {prepareTotalBugsByPriorityData().datasets.map(dataset => (
                          <TableCell key={dataset.label} align="right">{dataset.label}</TableCell>
                        ))}
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {releases.map((release, releaseIndex) => {
                        const chartData = prepareTotalBugsByPriorityData();
                        const rowTotal = chartData.datasets.reduce(
                          (sum, dataset) => sum + dataset.data[releaseIndex], 
                          0
                        );
                        
                        return (
                          <TableRow key={release.version}>
                            <TableCell>{release.version}</TableCell>
                            {chartData.datasets.map((dataset, datasetIndex) => (
                              <TableCell key={`${release.version}-${dataset.label}`} align="right">
                                {dataset.data[releaseIndex]}
                              </TableCell>
                            ))}
                            <TableCell align="right"><strong>{rowTotal}</strong></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Fix Version Distribution Row */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Description (Column 1 - 20%) */}
            <Grid item xs={12} md={2}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Fix Version Distribution
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Displays how bugs are distributed across different fix versions. Shows which releases have bugs assigned to them and helps track bug resolution planning.
                </Typography>
              </Paper>
            </Grid>
            
            {/* Chart (Column 2 - 40%) */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Fix Version Distribution (Grouped)
                </Typography>
                <Box sx={{ height: '320px', width: '100%', position: 'relative', pt: 2 }}>
                  <Bar 
                    data={prepareFixVersionDistributionData()} 
                    options={{
                      ...stackedBarOptions,
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        ...stackedBarOptions.plugins,
                        datalabels: {
                          display: false
                        }
                      }
                    }} 
                  />
                </Box>
              </Paper>
            </Grid>
            
            {/* Table (Column 3 - 40%) */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Fix Version Distribution
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Release</TableCell>
                        {prepareFixVersionDistributionData().datasets.map(dataset => (
                          <TableCell key={dataset.label} align="right">{dataset.label}</TableCell>
                        ))}
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {releases.map((release, releaseIndex) => {
                        const chartData = prepareFixVersionDistributionData();
                        const rowTotal = chartData.datasets.reduce(
                          (sum, dataset) => sum + dataset.data[releaseIndex], 
                          0
                        );
                        
                        return (
                          <TableRow key={release.version}>
                            <TableCell>{release.version}</TableCell>
                            {chartData.datasets.map((dataset, datasetIndex) => (
                              <TableCell key={`${release.version}-${dataset.label}`} align="right">
                                {dataset.data[releaseIndex]}
                              </TableCell>
                            ))}
                            <TableCell align="right"><strong>{rowTotal}</strong></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Status Distribution Row */}
          <Grid container spacing={3}>
            {/* Description (Column 1 - 20%) */}
            <Grid item xs={12} md={2}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Status Distribution
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Represents the breakdown of bugs by their current status (New, Active, Resolved, Closed, etc.) across releases. Tracks bug lifecycle and resolution progress.
                </Typography>
              </Paper>
            </Grid>
            
            {/* Chart (Column 2 - 40%) */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Status Distribution (Grouped)
                </Typography>
                <Box sx={{ height: '320px', width: '100%', position: 'relative', pt: 2 }}>
                  <Bar 
                    data={prepareStatusDistributionData()} 
                    options={{
                      ...stackedBarOptions,
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        ...stackedBarOptions.plugins,
                        datalabels: {
                          display: false
                        }
                      }
                    }} 
                  />
                </Box>
              </Paper>
            </Grid>
            
            {/* Table (Column 3 - 40%) */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h6" gutterBottom>
                  Status Distribution
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Release</TableCell>
                        {prepareStatusDistributionData().datasets.map(dataset => (
                          <TableCell key={dataset.label} align="right">{dataset.label}</TableCell>
                        ))}
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {releases.map((release, releaseIndex) => {
                        const chartData = prepareStatusDistributionData();
                        const rowTotal = chartData.datasets.reduce(
                          (sum, dataset) => sum + dataset.data[releaseIndex], 
                          0
                        );
                        
                        return (
                          <TableRow key={release.version}>
                            <TableCell>{release.version}</TableCell>
                            {chartData.datasets.map((dataset, datasetIndex) => (
                              <TableCell key={`${release.version}-${dataset.label}`} align="right">
                                {dataset.data[releaseIndex]}
                              </TableCell>
                            ))}
                            <TableCell align="right"><strong>{rowTotal}</strong></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default BugComparisonCharts;
